import React, { useState, useEffect, useContext } from 'react';
import { DropdownList } from 'react-widgets';
import moment from 'moment-timezone';
import axios from 'axios';

import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles, withStyles } from '@material-ui/core/styles';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import DeleteIcon from '@material-ui/icons/DeleteOutlineRounded';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';

import { Loading } from '../stories/Loading';
import PrimaryButton from './lib/buttons/PrimaryButton';
import { FeatureContext } from '../App';

interface AdvanceAmountProps {
  accountsView?: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: 'auto',
    textAlign: 'start',
    margin: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    textAlign: 'start',
    height: 'calc(100VH - 120px)',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100VW',
  },
  componentTitle: {
    color: '#142966',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: '24px',
    marginBottom: '0px',
  },
  separatorLine: {
    height: '2px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
    background: '#004878',
    border: 'none',
  },
  mainContainer: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      marginLeft: '40px',
      marginRight: '40px',
    },
    borderRadius: '4px',
    marginBottom: '12px',
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '12px',
  },
  formSeparator: {
    background: '#004878',
    width: '2px',
    border: 'none',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: '2px',
    },
    [theme.breakpoints.up('sm')]: {
      width: '2px',
      height: 'auto',
    },
  },
  button: {
    maxWidth: '320px',
    width: '100%',
    marginBottom: '24px',
    marginTop: '24px',
    [theme.breakpoints.up('sm')]: {
      alignSelf: 'center',
    },
  },
  disabledButton: {
    maxWidth: '320px',
    width: '100%',
    marginBottom: '24px',
    marginTop: '24px',
    [theme.breakpoints.up('sm')]: {
      alignSelf: 'center',
    },
    backgroundColor: 'gray',
  },
  particularsTable: {
    marginTop: '12px',
    marginBottom: '12px',
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
      alignSelf: 'center',
    },
  },
  deleteIcon: {
    justifyContent: 'center',
    fontSize: 32,
    '&:hover': {
      color: '#c23616',
    },
  },
  downloadIcon: {
    justifyContent: 'center',
    fontSize: 32,
    '&:hover': {
      color: '#c23616',
    },
  },
}));

const RedRadio = withStyles({
  root: {
    '&$checked': {
      color: '#c23616',
    },
  },
  checked: {},
})((props: { [x: string]: any }) => <Radio color="default" {...props} />);

const AdvanceAmount = (props: AdvanceAmountProps) => {
  const classes = useStyles();
  const features = useContext(FeatureContext);

  const accountsView = props.accountsView || false;
  const [loading, setLoading] = useState(false);
  const [refType, setRefType] = useState<string>();

  const [checkedRooms, setCheckedRooms] = useState(new Array<any>());
  const [advanceInfo, setAdvanceInfo] = useState(new Array<any>());

  const [selectedRoom, setSelectedRoom] = useState<any>();
  const [invoiceId, setInvoiceId] = useState<string>();

  const [advanceAmount, setAdvanceAmount] = useState<string>();
  const [advanceReceipt, setAdvanceReceipt] = useState<string>();
  const [advancePayMode, setAdvancePayMode] = useState<string>();

  useEffect(() => getAllCheckedRooms(), []);
  useEffect(
    () => fetchAdvanceAmountInfoForRoom(selectedRoom?.checkin_info_id),
    [selectedRoom]
  );

  const getAllCheckedRooms = () => {
    setLoading(true);

    axios
      .get('/api/getAllCheckedRooms')
      .then(({ data }) => {
        setCheckedRooms(data.checkedRooms || []);
      })
      .catch((e) => {
        alert('Error fetching data');
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchAdvanceAmountInfoForRoom = (checkinInfoId: number) => {
    if (checkinInfoId > 0) {
      setLoading(true);

      axios
        .get(`/api/advanceAmountInfo?checkin_info_id=${checkinInfoId}`)
        .then(({ data }) => setAdvanceInfo(data.results))
        .catch((e) => {
          alert('Failed to fetch advance amount info. Try again');
          console.error('Fetch Error: ', JSON.stringify(e));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const fetchAdvanceAmountInfoForInvoice = () => {
    if (invoiceId && invoiceId.length > 3) {
      setLoading(true);

      axios
        .get(`/api/advanceAmountInfo?invoice_id=${invoiceId}`)
        .then(({ data }) => setAdvanceInfo(data.results))
        .catch((e) => {
          alert('Failed to fetch advance amount info. Try again');
          console.error('Fetch Error: ', JSON.stringify(e));
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      alert('Invalid Invoice Id!');
    }
  };

  const deleteTransaction = (info: any) => {
    if (refType !== 'invoice') {
      const checkinInfoId = info.checkin_info_id;
      const transactionId = info.stay_transaction_id;
      setLoading(true);

      axios
        .post('/api/deleteStayTransaction', { checkinInfoId, transactionId })
        .then(({ data }) => {
          if (data.errno > 0) {
            alert(data.message || 'Some error occured. Try again');
          } else {
            fetchAdvanceAmountInfoForRoom(checkinInfoId);
          }
        })
        .catch((e) => {
          alert('Some request exception occurred.');
          console.error('Error: ' + JSON.stringify(e));
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const downloadStatement = (checkinInfoId?: number, invoiceId?: string) => {
    setLoading(true);
    axios
      .post(
        '/api/downloadAdvanceStatement',
        { checkinInfoId, invoiceId },
        {
          responseType: 'blob',
        }
      )
      .then(({ data }) => {
        if (data.errno > 0) {
          alert(data.message || 'Some error occured. Try again');
        } else {
          const blob = data;
          var file = new Blob([blob], { type: 'application/pdf' });
          var url = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = url;
          let fileName = checkinInfoId
            ? `PayStatement-R${selectedRoom.room_name}.pdf`
            : `PayStatement_${invoiceId}.pdf`;
          link.setAttribute('download', fileName);
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
        }
      })
      .catch((e) => {
        alert('Some request exception occurred.');
        console.error('Error: ' + JSON.stringify(e));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    if (!selectedRoom) {
      alert('Select a room');
    } else if (!(advanceAmount && advanceAmount.trim().length > 0)) {
      alert('Enter advance amount greater than 0');
    } else if (!(advanceReceipt && advanceReceipt.trim().length > 0)) {
      alert('Enter advance receipt number');
    } else if (!(advancePayMode && advancePayMode.trim().length > 0)) {
      alert('Select payment mode');
    } else {
      let checkinInfoId = selectedRoom.checkin_info_id;
      let parsedAdvanceAmount = parseInt(advanceAmount || '0');
      let parsedAdvanceReceipt = parseInt(advanceReceipt || '0');

      let dateTime = moment().local();
      let transactionDate = dateTime.format('YYYY-MM-DD');
      let transactionTime = dateTime.format('hh:mm:ss');

      if (parsedAdvanceAmount > 0 && parsedAdvanceReceipt > 0) {
        setLoading(true);
        axios
          .post('/api/registerAdvanceAmount', {
            checkin_info_id: checkinInfoId,
            advance_amount: parsedAdvanceAmount,
            advance_receipt_no: parsedAdvanceReceipt,
            advance_pay_mode: advancePayMode,
            transaction_receipt: advanceReceipt.trim(),
            transaction_date: transactionDate,
            transaction_time: transactionTime,
          })
          .then((res) => res.data)
          .then((res) => {
            if (res.errno > 0) {
              alert(res.message || 'Some error occured. Try again');
            } else {
              fetchAdvanceAmountInfoForRoom(checkinInfoId);
              setAdvanceAmount(undefined);
              setAdvanceReceipt(undefined);
              setAdvancePayMode(undefined);
            }
          })
          .catch((e) => {
            alert('Some request exception occurred.');
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        alert('Invalid Amount or Receipt info');
      }
    }
  };

  const getAdvanceTable = () => {
    if (advanceInfo && advanceInfo.length > 0) {
      const StyledTableCell = withStyles((theme) => ({
        head: {
          backgroundColor: theme.palette.common.black,
          color: theme.palette.common.white,
        },
        body: {
          fontSize: 16,
        },
      }))(TableCell);

      const StyledTableRow = withStyles((theme) => ({
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: theme.palette.action.hover,
          },
        },
      }))(TableRow);

      return (
        <TableContainer component={Paper} className={classes.particularsTable}>
          <Table aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Date</StyledTableCell>
                <StyledTableCell align="right">Receipt</StyledTableCell>
                <StyledTableCell align="right">Amount</StyledTableCell>
                <StyledTableCell align="right">PayMode</StyledTableCell>
                <StyledTableCell align="right">
                  {advanceInfo.length > 0 ? (
                    <DownloadIcon
                      onClick={(_) =>
                        downloadStatement(
                          selectedRoom?.checkin_info_id,
                          invoiceId
                        )
                      }
                      className={classes.downloadIcon}
                    />
                  ) : (
                    []
                  )}
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {advanceInfo.map((row) => (
                <StyledTableRow key={row.stay_transaction_id}>
                  <StyledTableCell width={'20%'}>
                    {row.transaction_date
                      ? moment(row.transaction_date).format('DD-MM-YYYY')
                      : 'N/A'}
                  </StyledTableCell>
                  <StyledTableCell align="right" width={'20%'}>
                    {row.transaction_receipt
                      ? row.transaction_receipt
                      : row.transaction_receipt_no}
                  </StyledTableCell>
                  <StyledTableCell align="right" width={'20%'}>
                    {row.transaction_amount}
                  </StyledTableCell>
                  <StyledTableCell align="right" width={'20%'}>
                    {row.payment_mode}
                  </StyledTableCell>
                  <StyledTableCell
                    align="right"
                    width={'25%'}
                    style={{ justifyItems: 'center' }}
                  >
                    {refType === 'room' && accountsView === false ? (
                      <DeleteIcon
                        onClick={(_) => deleteTransaction(row)}
                        className={classes.deleteIcon}
                      />
                    ) : (
                      []
                    )}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    } else {
      return '';
    }
  };

  const reset = () => {
    setSelectedRoom(undefined);
    setAdvanceInfo(new Array<any>());

    setAdvanceAmount(undefined);
    setAdvanceReceipt(undefined);
    setAdvancePayMode(undefined);
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <Loading />
      </div>
    );
  } else {
    return (
      <div className={classes.root}>
        <h2 className={classes.componentTitle}>Advance Amount</h2>
        <hr className={classes.separatorLine} />
        <div className={classes.mainContainer}>
          <div className={classes.formContainer}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Ref Type</FormLabel>
              <RadioGroup
                aria-label="Reference Type"
                value={refType || null}
                onChange={(e) => {
                  reset();
                  setRefType(e.target.value);
                  setInvoiceId(undefined);
                }}
              >
                <FormControlLabel
                  value="room"
                  control={<RedRadio />}
                  label="Room"
                />
                <FormControlLabel
                  value="invoice"
                  control={<RedRadio />}
                  label="Invoice"
                />
              </RadioGroup>
            </FormControl>
            <Autocomplete
              options={checkedRooms.sort((a, b) => a.room_name - b.room_name)}
              groupBy={(r: any) =>
                features.groupRoomsByBlockFeatureEnabled
                  ? r.block_name
                  : r.floor_str
              }
              getOptionLabel={(r: any) => r.room_name}
              disabled={refType !== 'room'}
              style={{ width: 300, marginTop: '24px', marginBottom: '24px' }}
              renderInput={(params: { [x: string]: any }) => (
                <TextField {...params} label="Room No" variant="outlined" />
              )}
              onChange={(_, r) => {
                if (r != null) {
                  setSelectedRoom(r);
                } else {
                  setSelectedRoom(undefined);
                  setAdvanceInfo([]);
                }
              }}
              value={selectedRoom || null}
            />
            <TextField
              label="Invoice No"
              variant="outlined"
              placeholder="Ex: 1-21/22"
              disabled={refType !== 'invoice'}
              value={invoiceId || ''}
              onChange={(e) => {
                reset();
                setInvoiceId(e.target.value);
              }}
              style={{ width: 300 }}
            />
            <PrimaryButton
              onClick={fetchAdvanceAmountInfoForInvoice}
              disabled={refType !== 'invoice'}
              className={
                refType !== 'invoice' ? classes.disabledButton : classes.button
              }
            >
              Fetch
            </PrimaryButton>
          </div>
          <hr className={classes.formSeparator} hidden={accountsView} />
          {accountsView ? (
            []
          ) : (
            <div className={classes.formContainer}>
              <label
                style={{
                  margin: '8px',
                  marginBottom: '32px',
                  textDecoration: 'underline',
                }}
              >
                Payment Details
              </label>
              <TextField
                label="Advance Amount"
                variant="outlined"
                placeholder="Ex: 500"
                value={advanceAmount || ''}
                disabled={refType !== 'room'}
                onChange={(e) => setAdvanceAmount(e.target.value)}
                style={{ width: 300 }}
              />
              <TextField
                label="Advance Receipt"
                variant="outlined"
                placeholder="Ex: 123"
                value={advanceReceipt || ''}
                disabled={refType !== 'room'}
                onChange={(e) => setAdvanceReceipt(e.target.value)}
                style={{ width: 300, marginTop: '32px' }}
              />

              <DropdownList
                style={{
                  maxWidth: '300px',
                  display: 'inline-block',
                  marginTop: '32px',
                }}
                disabled={refType !== 'room'}
                data={['Cash', 'C.C', 'UPI', 'Other']}
                onChange={(r) => setAdvancePayMode(r)}
                value={advancePayMode || 'Payment Mode'}
              />

              <PrimaryButton
                disabled={refType !== 'room'}
                onClick={handleSubmit}
                className={
                  refType !== 'room' ? classes.disabledButton : classes.button
                }
              >
                Save
              </PrimaryButton>
            </div>
          )}
        </div>
        <hr className={classes.separatorLine} />
        {getAdvanceTable()}
      </div>
    );
  }
};

export default AdvanceAmount;
